/**
 * CanvaLoop top navigation.
 *
 * Higgsfield/Lovable horizontal layout:
 *   [logo]  [route pills]  …  [workspace switcher · theme · avatar]
 *
 * - Cream background, 1px subtle border on scroll.
 * - Center pills shift to a hamburger menu below 1024px.
 * - Workspace + user dropdowns open below the trigger.
 * - Sticky to top of viewport.
 */

import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  Menu, X, ChevronDown, Plus, LogOut, Settings, Heart, Building2, Sun, Moon,
  LayoutGrid, Image as ImageIcon, Calendar as CalendarIcon,
  Briefcase, Layers, Users, Crown, Shield, Brush, Zap, ExternalLink, Sparkles,
} from "lucide-react";

import BrandLogo from "@/components/BrandLogo";
import CreditPill from "@/components/CreditPill";
import { ROUTES, type AppRoute, type PlanTier } from "@/types";
import { useTheme } from "@/hooks/useTheme";
import { useWorkspace } from "@/hooks/useWorkspace";
import { useAuth } from "@/hooks/useAuth";
import { useCredits } from "@/hooks/useCredits";
import { initials } from "@/utils/format";
import { optimizedUrl, optimizedSrcSet } from "@/lib/files";
import { prefetchRoute } from "@/lib/routePrefetch";

// Start the chunk download the moment a pointer enters the link. Also fires
// on focus (keyboard users) and on touchstart (mobile).
const hoverPrefetch = (to: AppRoute) => ({
  onMouseEnter: () => prefetchRoute(to),
  onFocus:      () => prefetchRoute(to),
  onTouchStart: () => prefetchRoute(to),
});

/**
 * Main top-nav items — visible to every signed-in user.
 *
 * Phase 0: Studio route removed (Dashboard PromptDeck is the only
 * generation surface; /studio 301-redirects to /dashboard).
 *
 * Templates is intentionally absent: it's admin-authoring surface, shown
 * only in the avatar dropdown for system admins. Editor is the user-
 * facing masked-inpaint tool.
 */
const NAV_ITEMS = [
  { to: ROUTES.DASHBOARD,   label: "Create",     icon: LayoutGrid },
  { to: ROUTES.EDITOR,      label: "Editor",     icon: Brush },
  { to: ROUTES.LIBRARY,     label: "Library",    icon: ImageIcon },
  { to: ROUTES.CALENDAR,    label: "Calendar",   icon: CalendarIcon },
  { to: ROUTES.BRAND_KIT,   label: "Brand kit",  icon: Briefcase },
  { to: ROUTES.TEAM,        label: "Team",       icon: Users },
];

export const TopNav: React.FC = () => {
  const { pathname } = useLocation();
  const navigate     = useNavigate();
  const { user, logout } = useAuth();
  const { workspaces, active, switchTo } = useWorkspace();
  const { theme, toggle: toggleTheme } = useTheme();
  const { data: credits } = useCredits();

  const [scrolled,  setScrolled]  = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [wsOpen,    setWsOpen]    = useState(false);
  const [userOpen,  setUserOpen]  = useState(false);
  // Display labels for owners of workspaces where the current user isn't
  // the owner — drives the small "by <name>" chip in the switcher.
  const [ownerLabels, setOwnerLabels] = useState<Record<string, string>>({});

  // Fetch owner labels when the list of non-owned workspaces changes.
  // Uses the /api/cl/members/labels server helper (users.listRule is
  // locked to self + admin so we can't query the users collection directly).
  //
  // A quick optimistic fallback: if `w.expand?.userId?.displayName/email` is
  // present (admin view of workspaces uses this expand), we can render the
  // chip immediately without waiting on the round-trip. The server fetch
  // then fills in any gaps.
  useEffect(() => {
    if (!user || workspaces.length === 0) return;

    // Seed from whatever's already on the workspace records.
    const seeded: Record<string, string> = {};
    for (const w of workspaces) {
      if (w.userId === user.id) continue;
      const exp = (w as unknown as { expand?: { userId?: { displayName?: string; name?: string; email?: string } } }).expand?.userId;
      const label = exp?.displayName || exp?.name || (exp?.email ? exp.email.split("@")[0] : undefined);
      if (label) seeded[w.userId] = label;
    }
    if (Object.keys(seeded).length) {
      setOwnerLabels((prev) => ({ ...prev, ...seeded }));
    }

    const foreignOwnerIds = Array.from(new Set(
      workspaces.filter((w) => w.userId !== user.id).map((w) => w.userId),
    ));
    if (foreignOwnerIds.length === 0) return;
    let cancelled = false;
    import("@/lib/pb").then(({ pb }) =>
      pb.send<{ labels: Record<string, string> }>("/api/cl/members/labels", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ userIds: foreignOwnerIds }),
      })
    ).then((res) => {
      if (!cancelled && res?.labels) {
        setOwnerLabels((prev) => ({ ...prev, ...res.labels }));
      }
    }).catch(() => { /* silent — optimistic seed covers us */ });
    return () => { cancelled = true; };
  }, [user?.id, workspaces.map((w) => w.userId).join(",")]);

  // Scrolled border
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 4);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Click-outside for dropdowns
  const wsRef   = useRef<HTMLDivElement>(null);
  const userRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      if (!wsRef.current?.contains(e.target as Node))   setWsOpen(false);
      if (!userRef.current?.contains(e.target as Node)) setUserOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  // Close mobile menu on route change
  useEffect(() => { setMobileOpen(false); }, [pathname]);

  const isActive = (to: string) =>
    pathname === to || (to !== ROUTES.DASHBOARD && pathname.startsWith(to));

  return (
    <header
      style={{
        position: "sticky",
        top: 0,
        zIndex: "var(--z-sticky)" as unknown as number,
        background: scrolled
          ? "color-mix(in srgb, var(--bg-page) 88%, transparent)"
          : "var(--bg-page)",
        backdropFilter: scrolled ? "blur(14px) saturate(140%)" : "none",
        WebkitBackdropFilter: scrolled ? "blur(14px) saturate(140%)" : "none",
        borderBottom: scrolled ? "1px solid var(--border-default)" : "1px solid transparent",
        transition: "background 200ms, border-color 200ms",
      }}
    >
      <div
        style={{
          maxWidth: "var(--page-max-width)",
          margin: "0 auto",
          padding: "0 var(--page-padding-x)",
          height: "var(--topnav-height)",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 16,
        }}
      >
        {/* ── Left: logo + nav pills ─────────────────────────── */}
        <div style={{ display: "flex", alignItems: "center", gap: 32, minWidth: 0 }}>
          <Link
            to={ROUTES.DASHBOARD}
            aria-label="CanvaLoop home"
            style={{ textDecoration: "none", display: "inline-flex" }}
          >
            <BrandLogo size={20} />
          </Link>

          {/* Desktop pills */}
          <nav className="cl-nav-pills" style={{
            display: "none",
            alignItems: "center",
            gap: 2,
            background: "var(--bg-surface-alt)",
            border: "1px solid var(--border-default)",
            borderRadius: "var(--radius-pill)",
            padding: 3,
          }}>
            {NAV_ITEMS.map((item) => {
              const active = isActive(item.to);
              const Icon = item.icon;
              return (
                <Link
                  key={item.to}
                  to={item.to}
                  {...hoverPrefetch(item.to as AppRoute)}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 6,
                    padding: "6px 14px",
                    height: 30,
                    borderRadius: "var(--radius-pill)",
                    fontSize: "var(--font-size-sm)",
                    fontWeight: active ? 600 : 500,
                    color: active ? "var(--text-primary)" : "var(--text-tertiary)",
                    background: active ? "var(--bg-surface-raised)" : "transparent",
                    boxShadow: active ? "var(--shadow-xs)" : "none",
                    textDecoration: "none",
                    transition: "var(--transition-fast)",
                  }}
                >
                  <Icon size={13} strokeWidth={1.5} style={{ color: active ? "var(--accent-300)" : "currentColor" }} />
                  {item.label}
                </Link>
              );
            })}
          </nav>
        </div>

        {/* ── Right: workspace · theme · avatar ──────────────── */}
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {/* Workspace switcher */}
          <div ref={wsRef} style={{ position: "relative" }} className="cl-nav-ws">
            <button
              onClick={() => setWsOpen((v) => !v)}
              aria-label="Switch workspace"
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                height: 36,
                padding: "0 10px 0 6px",
                background: "var(--bg-surface-alt)",
                border: "1px solid var(--border-default)",
                borderRadius: "var(--radius-pill)",
                fontSize: "var(--font-size-sm)",
                color: "var(--text-primary)",
                fontWeight: 500,
                cursor: "pointer",
                transition: "var(--transition-fast)",
                maxWidth: 180,
              }}
            >
              <span style={{
                width: 24, height: 24,
                borderRadius: "var(--radius-md)",
                background: "var(--bg-surface)",
                border: "1px solid var(--border-default)",
                display: "inline-flex", alignItems: "center", justifyContent: "center",
                overflow: "hidden",
                flexShrink: 0,
              }}>
                {active?.logo ? (
                  <img
                    src={optimizedUrl(active, active.logo, { w: 48, q: 82 })}
                    srcSet={optimizedSrcSet(active, active.logo, 48, { q: 82 })}
                    alt="" decoding="async"
                    style={{ width: "100%", height: "100%", objectFit: "cover" }}
                  />
                ) : (
                  <span style={{ fontSize: 11, fontWeight: 600, color: "var(--text-tertiary)" }}>
                    {initials(active?.name ?? "?")}
                  </span>
                )}
              </span>
              <span style={{
                whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
              }}>
                {active?.name ?? "No workspace"}
              </span>
              <ChevronDown size={12} strokeWidth={1.5} style={{ opacity: 0.5, flexShrink: 0 }} />
            </button>

            {wsOpen && (
              <div className="animate-dropdown-in" style={{
                position: "absolute", right: 0, top: "calc(100% + 8px)",
                width: 280,
                background: "var(--bg-surface-raised)",
                border: "1px solid var(--border-default)",
                borderRadius: "var(--radius-xl)",
                boxShadow: "var(--shadow-dropdown)",
                padding: 8,
                zIndex: "var(--z-dropdown)" as unknown as number,
              }}>
                <div style={{
                  padding: "8px 12px",
                  fontFamily: "var(--font-mono)",
                  fontSize: "var(--font-size-xs)",
                  color: "var(--text-tertiary)",
                  textTransform: "uppercase",
                  letterSpacing: "var(--tracking-label)",
                }}>
                  Workspaces
                </div>
                <div style={{ maxHeight: 280, overflowY: "auto" }}>
                  {workspaces.map((w) => (
                    <button
                      key={w.id}
                      onClick={() => { switchTo(w); setWsOpen(false); }}
                      style={{
                        display: "flex", alignItems: "center", gap: 12,
                        width: "100%", padding: "10px 12px",
                        background: w.id === active?.id ? "var(--bg-surface-alt)" : "transparent",
                        border: "none", borderRadius: "var(--radius-md)",
                        cursor: "pointer", textAlign: "left",
                        transition: "var(--transition-fast)",
                      }}
                    >
                      <span style={{
                        width: 32, height: 32,
                        borderRadius: "var(--radius-md)",
                        background: "var(--bg-surface)",
                        border: "1px solid var(--border-default)",
                        display: "inline-flex", alignItems: "center", justifyContent: "center",
                        overflow: "hidden", flexShrink: 0,
                      }}>
                        {w.logo ? (
                          <img
                            src={optimizedUrl(w, w.logo, { w: 64, q: 82 })}
                            srcSet={optimizedSrcSet(w, w.logo, 64, { q: 82 })}
                            alt="" decoding="async"
                            style={{ width: "100%", height: "100%", objectFit: "cover" }}
                          />
                        ) : (
                          <span style={{ fontSize: 12, fontWeight: 600, color: "var(--text-tertiary)" }}>
                            {initials(w.name)}
                          </span>
                        )}
                      </span>
                      <span style={{ flex: 1, minWidth: 0 }}>
                        <span style={{
                          display: "flex", alignItems: "center", gap: 6,
                          fontSize: "var(--font-size-sm)", fontWeight: 600,
                          color: "var(--text-primary)",
                        }}>
                          <span style={{
                            overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                            minWidth: 0,
                          }}>
                            {w.name}
                          </span>
                          {/* Owner chip: shown only when this workspace is
                              shared *to* the current user (not theirs). Uses
                              the brand gradient so it reads as a CanvaLoop
                              accent, not a stray tag. */}
                          {user && w.userId !== user.id && (() => {
                            const label = ownerLabels[w.userId];
                            return (
                              <span
                                title={label ? `Owned by ${label}` : "Shared with you"}
                                style={{
                                  display: "inline-flex", alignItems: "center", gap: 4,
                                  padding: "2px 8px",
                                  background: "var(--brand-gradient)",
                                  color: "#fff",
                                  borderRadius: "var(--radius-pill)",
                                  fontFamily: "var(--font-mono)",
                                  fontSize: 9, fontWeight: 700,
                                  textTransform: "uppercase",
                                  letterSpacing: "var(--tracking-label)",
                                  whiteSpace: "nowrap",
                                  flexShrink: 0,
                                  boxShadow: "0 4px 10px -4px rgba(255,77,109,0.55)",
                                }}
                              >
                                {label ? `by ${label}` : "Shared"}
                              </span>
                            );
                          })()}
                        </span>
                        {w.category && (
                          <span style={{
                            display: "block",
                            fontSize: "var(--font-size-xs)",
                            color: "var(--text-tertiary)",
                          }}>
                            {w.category}
                          </span>
                        )}
                      </span>
                    </button>
                  ))}
                </div>
                <div style={{
                  borderTop: "1px solid var(--border-default)",
                  marginTop: 6, paddingTop: 6,
                }}>
                  <button
                    onClick={() => { setWsOpen(false); navigate(ROUTES.WORKSPACES); }}
                    style={{
                      display: "flex", alignItems: "center", gap: 10,
                      width: "100%", padding: "10px 12px",
                      background: "transparent", border: "none",
                      borderRadius: "var(--radius-md)",
                      cursor: "pointer", textAlign: "left",
                      fontSize: "var(--font-size-sm)", fontWeight: 500,
                      color: "var(--accent-300)",
                    }}
                  >
                    <Plus size={14} strokeWidth={1.5} />
                    New workspace
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Credit balance pill — Phase 0 addition.
              Always visible for authenticated users. Source: useCredits
              hook. Hover shows usage breakdown; click opens a dropdown
              with manage-billing + top-up CTAs. */}
          <CreditPill />

          {/* Theme toggle */}
          <button
            onClick={toggleTheme}
            aria-label={`Switch to ${theme === "light" ? "dark" : "light"} theme`}
            style={{
              width: 36, height: 36,
              background: "var(--bg-surface-alt)",
              border: "1px solid var(--border-default)",
              borderRadius: "var(--radius-pill)",
              display: "inline-flex", alignItems: "center", justifyContent: "center",
              color: "var(--text-secondary)",
              cursor: "pointer",
              transition: "var(--transition-fast)",
            }}
          >
            {theme === "light" ? <Moon size={14} strokeWidth={1.5} /> : <Sun size={14} strokeWidth={1.5} />}
          </button>

          {/* User avatar dropdown.
              Previous attempt at an admin "ring" used a double
              linear-gradient on the avatar button itself, which broke
              the fallback brand-gradient for users without an uploaded
              avatar AND clipped the admin shield badge behind
              overflow:hidden. Structure now:
                <div.wrapper>   ← relatively positioned, NOT clipped
                  <button.avatar>  ← overflow:hidden so the image rounds
                  <span.admin-badge>  ← sibling, rides outside the avatar
              */}
          <div ref={userRef} style={{ position: "relative" }}>
            <div style={{ position: "relative", lineHeight: 0 }}>
              <button
                onClick={() => setUserOpen((v) => !v)}
                aria-label="Account menu"
                style={{
                  width: 36, height: 36,
                  background: user?.avatar ? "transparent" : "var(--brand-gradient)",
                  border: "1px solid var(--border-default)",
                  // Admin accounts get a subtle gradient-coloured outer
                  // halo via box-shadow instead of a border trick, so
                  // nothing interferes with the inner fallback background.
                  boxShadow: user?.isSystemAdmin
                    ? "0 0 0 2px var(--bg-page), 0 0 0 3.5px color-mix(in srgb, var(--accent-300) 70%, #FF8E53)"
                    : undefined,
                  borderRadius: "var(--radius-pill)",
                  overflow: "hidden",
                  cursor: "pointer",
                  display: "inline-flex", alignItems: "center", justifyContent: "center",
                  color: "#fff",
                  fontSize: "var(--font-size-xs)", fontWeight: 600,
                  fontFamily: "var(--font-display)",
                  padding: 0,
                }}
              >
                {user?.avatar ? (
                  <img
                    src={optimizedUrl(user, user.avatar, { w: 72, q: 82 })}
                    srcSet={optimizedSrcSet(user, user.avatar, 72, { q: 82 })}
                    alt="" decoding="async"
                    style={{ width: "100%", height: "100%", objectFit: "cover" }}
                  />
                ) : (
                  initials(user?.displayName || user?.name || user?.email || "?")
                )}
              </button>
              {user?.isSystemAdmin && (
                <span
                  aria-label="Admin"
                  title="System admin"
                  style={{
                    position: "absolute",
                    right: -3, bottom: -3,
                    width: 15, height: 15,
                    borderRadius: "50%",
                    background: "var(--brand-gradient)",
                    border: "2px solid var(--bg-page)",
                    display: "inline-flex", alignItems: "center", justifyContent: "center",
                    color: "#fff",
                    boxShadow: "0 2px 6px rgba(197,63,255,0.45)",
                    pointerEvents: "none",
                    zIndex: 2,
                  }}
                >
                  <Shield size={7} strokeWidth={3} />
                </span>
              )}
            </div>

            {userOpen && (
              <div className="animate-dropdown-in" style={{
                position: "absolute", right: 0, top: "calc(100% + 8px)",
                width: 288,
                background: "var(--bg-surface-raised)",
                border: "1px solid var(--border-default)",
                borderRadius: "var(--radius-xl)",
                boxShadow: "var(--shadow-dropdown)",
                padding: 8,
                zIndex: "var(--z-dropdown)" as unknown as number,
              }}>
                {/* Header — name + email + admin chip */}
                <div style={{
                  padding: "10px 12px 12px",
                  borderBottom: "1px solid var(--border-default)",
                  marginBottom: 8,
                }}>
                  <div style={{
                    display: "flex", alignItems: "center", gap: 6,
                    fontSize: "var(--font-size-sm)", fontWeight: 600,
                    color: "var(--text-primary)",
                  }}>
                    <span style={{
                      flex: 1, minWidth: 0,
                      whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                    }}>
                      {user?.displayName || user?.name || "Creator"}
                    </span>
                    {user?.isSystemAdmin && (
                      <span
                        title="System admin"
                        style={{
                          display: "inline-flex", alignItems: "center", gap: 3,
                          padding: "2px 8px",
                          borderRadius: "var(--radius-pill)",
                          background: "var(--brand-gradient)",
                          color: "#fff",
                          fontFamily: "var(--font-mono)",
                          fontSize: 9, fontWeight: 700,
                          textTransform: "uppercase",
                          letterSpacing: "var(--tracking-label)",
                          boxShadow: "0 3px 8px -3px rgba(197,63,255,0.5)",
                          flexShrink: 0,
                        }}
                      >
                        <Shield size={8} strokeWidth={2.5} />
                        Admin
                      </span>
                    )}
                  </div>
                  <div style={{
                    fontSize: "var(--font-size-xs)",
                    color: "var(--text-tertiary)",
                    whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                  }}>
                    {user?.email}
                  </div>
                </div>

                {/* Subscription status card — the explicit "Plan: X · N credits"
                    status row the user said was missing. Live via useCredits. */}
                <PlanStatusCard
                  data={credits}
                  onUpgrade={() => { setUserOpen(false); navigate(ROUTES.PLANS); }}
                  onManage={() => {
                    setUserOpen(false);
                    if (credits?.manageUrl) {
                      window.open(credits.manageUrl, "_blank", "noopener,noreferrer");
                    } else {
                      navigate(ROUTES.PLANS);
                    }
                  }}
                />

                <UserMenuItem icon={<Heart size={14} strokeWidth={1.5} />} prefetch={ROUTES.INSPIRATION} onClick={() => { setUserOpen(false); navigate(ROUTES.INSPIRATION); }}>
                  Saved inspiration
                </UserMenuItem>
                <UserMenuItem icon={<Building2 size={14} strokeWidth={1.5} />} prefetch={ROUTES.WORKSPACES} onClick={() => { setUserOpen(false); navigate(ROUTES.WORKSPACES); }}>
                  Manage workspaces
                </UserMenuItem>
                <UserMenuItem icon={<Crown size={14} strokeWidth={1.5} />} prefetch={ROUTES.PLANS} onClick={() => { setUserOpen(false); navigate(ROUTES.PLANS); }}>
                  Plans &amp; billing
                </UserMenuItem>
                <UserMenuItem icon={<Settings size={14} strokeWidth={1.5} />} prefetch={ROUTES.SETTINGS} onClick={() => { setUserOpen(false); navigate(ROUTES.SETTINGS); }}>
                  Account settings
                </UserMenuItem>
                {user?.isSystemAdmin && (
                  <>
                    <div style={{
                      borderTop: "1px solid var(--border-default)",
                      marginTop: 6, paddingTop: 6,
                    }} />
                    <UserMenuItem icon={<Layers size={14} strokeWidth={1.5} />} prefetch={ROUTES.TEMPLATES} onClick={() => { setUserOpen(false); navigate(ROUTES.TEMPLATES); }}>
                      Templates
                    </UserMenuItem>
                    <UserMenuItem icon={<Shield size={14} strokeWidth={1.5} />} prefetch={ROUTES.ADMIN} onClick={() => { setUserOpen(false); navigate(ROUTES.ADMIN); }}>
                      Admin console
                    </UserMenuItem>
                  </>
                )}
                <div style={{
                  borderTop: "1px solid var(--border-default)",
                  marginTop: 6, paddingTop: 6,
                }}>
                  <UserMenuItem icon={<LogOut size={14} strokeWidth={1.5} />} onClick={() => { setUserOpen(false); logout(); }} danger>
                    Sign out
                  </UserMenuItem>
                </div>
              </div>
            )}
          </div>

          {/* Mobile hamburger */}
          <button
            onClick={() => setMobileOpen((v) => !v)}
            aria-label="Toggle menu"
            className="cl-nav-hamburger"
            style={{
              display: "none",
              width: 36, height: 36,
              background: "var(--bg-surface-alt)",
              border: "1px solid var(--border-default)",
              borderRadius: "var(--radius-sm)",
              alignItems: "center", justifyContent: "center",
              color: "var(--text-secondary)",
              cursor: "pointer",
            }}
          >
            {mobileOpen ? <X size={16} strokeWidth={1.5} /> : <Menu size={16} strokeWidth={1.5} />}
          </button>
        </div>
      </div>

      {/* Mobile menu drawer */}
      {mobileOpen && (
        <div className="cl-nav-mobile-menu animate-fade-in-down" style={{
          background: "var(--bg-surface)",
          borderTop: "1px solid var(--border-default)",
          padding: "12px var(--page-padding-x)",
        }}>
          <nav style={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {NAV_ITEMS.map((item) => {
              const active = isActive(item.to);
              const Icon = item.icon;
              return (
                <Link
                  key={item.to}
                  to={item.to}
                  {...hoverPrefetch(item.to as AppRoute)}
                  style={{
                    display: "flex", alignItems: "center", gap: 12,
                    padding: "12px 14px",
                    borderRadius: "var(--radius-md)",
                    background: active ? "var(--bg-surface-alt)" : "transparent",
                    color: active ? "var(--text-primary)" : "var(--text-secondary)",
                    fontSize: "var(--font-size-md)",
                    fontWeight: active ? 600 : 500,
                    textDecoration: "none",
                  }}
                >
                  <Icon size={16} strokeWidth={1.5} style={{ color: active ? "var(--accent-300)" : "currentColor" }} />
                  {item.label}
                </Link>
              );
            })}
          </nav>
        </div>
      )}

      <style>{`
        @media (min-width: 1024px) {
          .cl-nav-pills { display: inline-flex !important; }
          .cl-nav-hamburger { display: none !important; }
        }
        @media (max-width: 1023px) {
          .cl-nav-pills, .cl-nav-mobile-menu-desktop { display: none !important; }
          .cl-nav-hamburger { display: inline-flex !important; }
          .cl-nav-ws { display: none; }
        }
      `}</style>
    </header>
  );
};

/**
 * PlanStatusCard — the "explicit plan + credits" status row inside the
 * avatar dropdown. v2.0.1 addition (the user couldn't see what they
 * subscribed for, by their own words).
 *
 * Layout:
 *   ┌─────────────────────────────────────────────┐
 *   │ [PRO] CanvaLoop Pro              [Manage →]│
 *   │ 500 of 500 generations this month           │
 *   │ ▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓ 0% used                │
 *   │ Renews 12 Jun 2026                          │
 *   └─────────────────────────────────────────────┘
 *
 * For Free users: same shell, "Upgrade to Pro" primary button at bottom
 * instead of "Manage". Daily-pool + monthly-cap shown.
 *
 * For loading state: 32px skeleton with no flash of empty plan.
 */
const PlanStatusCard: React.FC<{
  data: ReturnType<typeof useCredits>["data"];
  onUpgrade: () => void;
  onManage: () => void;
}> = ({ data, onUpgrade, onManage }) => {
  if (!data) {
    return (
      <div style={{
        margin: "0 4px 8px",
        padding: 12,
        background: "var(--bg-surface-alt)",
        border: "1px solid var(--border-default)",
        borderRadius: "var(--radius-md)",
        fontSize: 11,
        color: "var(--text-tertiary)",
        textAlign: "center",
      }}>
        Loading plan…
      </div>
    );
  }

  const plan: PlanTier = data.plan;
  const isPaid = plan !== "free";
  const planName = plan === "studio" ? "Studio" : plan === "pro" ? "Pro" : "Free";
  const longName = plan === "studio" ? "CanvaLoop Studio" : plan === "pro" ? "CanvaLoop Pro" : "Free plan";
  const PlanIcon = plan === "studio" ? Crown : plan === "pro" ? Zap : Sparkles;

  // Live count — for paid uses the balance row, for free uses today's row.
  const remaining = data.balance.remaining;
  const total     = data.balance.granted + (data.balance.bonusGranted || 0);
  const used      = data.balance.used;
  const fillPct   = total > 0 ? Math.min(100, (used / total) * 100) : 0;

  const periodLabel = data.balance.resetCadence === "monthly" ? "this month" : "today";

  const renewsAt = isPaid && data.currentPeriodEnd
    ? new Date(data.currentPeriodEnd).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })
    : null;

  return (
    <div style={{
      margin: "0 4px 8px",
      padding: 12,
      background: isPaid
        ? "linear-gradient(135deg, rgba(255,107,157,0.08), rgba(197,63,255,0.06))"
        : "var(--bg-surface-alt)",
      border: isPaid
        ? "1px solid color-mix(in srgb, var(--accent-300) 30%, transparent)"
        : "1px solid var(--border-default)",
      borderRadius: "var(--radius-md)",
    }}>
      {/* Plan chip + name + manage button */}
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        gap: 8, marginBottom: 10,
      }}>
        <div style={{ display: "inline-flex", alignItems: "center", gap: 8, minWidth: 0 }}>
          <span style={{
            display: "inline-flex", alignItems: "center", gap: 4,
            padding: "3px 8px",
            background: isPaid ? "var(--brand-gradient)" : "var(--bg-surface-raised)",
            color: isPaid ? "#fff" : "var(--text-tertiary)",
            borderRadius: "var(--radius-pill)",
            fontFamily: "var(--font-mono)",
            fontSize: 9, fontWeight: 800,
            textTransform: "uppercase", letterSpacing: "var(--tracking-label)",
            flexShrink: 0,
            boxShadow: isPaid ? "0 4px 10px -4px rgba(255,77,109,0.4)" : "none",
          }}>
            <PlanIcon size={9} strokeWidth={2.25} />
            {planName}
          </span>
          <span style={{
            fontSize: "var(--font-size-sm)", fontWeight: 600,
            color: "var(--text-primary)",
            whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
            minWidth: 0,
          }}>
            {longName}
          </span>
        </div>
        {isPaid && (
          <button
            onClick={onManage}
            aria-label="Manage subscription"
            title="Open billing portal"
            style={{
              display: "inline-flex", alignItems: "center", gap: 3,
              padding: "4px 8px",
              background: "transparent",
              color: "var(--text-secondary)",
              border: "1px solid var(--border-default)",
              borderRadius: "var(--radius-sm)",
              fontSize: 10, fontWeight: 600,
              cursor: "pointer",
              flexShrink: 0,
            }}
          >
            Manage <ExternalLink size={9} strokeWidth={2} />
          </button>
        )}
      </div>

      {/* Credit count */}
      <div style={{
        display: "flex", alignItems: "baseline", gap: 6,
        marginBottom: 6,
        color: "var(--text-primary)",
        fontSize: "var(--font-size-sm)",
        fontVariantNumeric: "tabular-nums",
      }}>
        <strong style={{ fontWeight: 700 }}>{remaining.toLocaleString()}</strong>
        <span style={{ color: "var(--text-tertiary)", fontWeight: 500 }}>
          of {total.toLocaleString()} generations {periodLabel}
        </span>
      </div>

      {/* Progress bar */}
      <div style={{
        position: "relative", height: 4,
        background: "color-mix(in srgb, var(--text-tertiary) 12%, transparent)",
        borderRadius: 2, overflow: "hidden",
        marginBottom: isPaid || data.monthly.cap > 0 || !isPaid ? 8 : 0,
      }}>
        <div style={{
          position: "absolute", left: 0, top: 0, bottom: 0,
          width: `${fillPct}%`,
          background: remaining <= 0 ? "var(--status-danger)"
                    : remaining / Math.max(total, 1) < 0.20 ? "var(--status-warning)"
                    : "var(--brand-gradient)",
          transition: "width 320ms var(--ease-smooth)",
        }} />
      </div>

      {/* Free: monthly secondary line */}
      {!isPaid && data.monthly.cap > 0 && (
        <div style={{
          display: "flex", justifyContent: "space-between",
          fontSize: 10,
          fontFamily: "var(--font-mono)",
          color: "var(--text-tertiary)",
        }}>
          <span>This month</span>
          <span style={{ fontVariantNumeric: "tabular-nums" }}>
            {data.monthly.used} / {data.monthly.cap}
          </span>
        </div>
      )}

      {/* Paid: renewal date */}
      {isPaid && renewsAt && (
        <div style={{
          fontSize: 10,
          color: "var(--text-tertiary)",
          fontFamily: "var(--font-mono)",
        }}>
          {data.cancelledAt ? "Ends" : "Renews"} {renewsAt}
        </div>
      )}

      {/* Free: upgrade nudge */}
      {!isPaid && (
        <button
          onClick={onUpgrade}
          style={{
            display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6,
            width: "100%", height: 32,
            marginTop: 10,
            background: "var(--brand-gradient)",
            color: "#fff",
            border: "none",
            borderRadius: "var(--radius-sm)",
            fontSize: "var(--font-size-xs)", fontWeight: 600,
            cursor: "pointer",
            boxShadow: "0 6px 16px -8px rgba(255,77,109,0.45)",
          }}
        >
          <Zap size={11} strokeWidth={2} />
          Upgrade to Pro
        </button>
      )}
    </div>
  );
};

const UserMenuItem: React.FC<{
  icon: React.ReactNode;
  onClick: () => void;
  children: React.ReactNode;
  danger?: boolean;
  /** Route whose chunk should be prefetched on hover. */
  prefetch?: AppRoute;
}> = ({ icon, onClick, children, danger, prefetch }) => (
  <button
    onClick={onClick}
    onFocus={prefetch ? () => prefetchRoute(prefetch) : undefined}
    onMouseEnter={(e) => {
      if (prefetch) prefetchRoute(prefetch);
      e.currentTarget.style.background = danger ? "var(--status-danger-bg)" : "var(--bg-surface-alt)";
      e.currentTarget.style.color = danger ? "var(--status-danger)" : "var(--text-primary)";
    }}
    onMouseLeave={(e) => {
      e.currentTarget.style.background = "transparent";
      e.currentTarget.style.color = danger ? "var(--status-danger)" : "var(--text-secondary)";
    }}
    style={{
      display: "flex", alignItems: "center", gap: 10,
      width: "100%", padding: "10px 12px",
      background: "transparent", border: "none",
      borderRadius: "var(--radius-md)",
      cursor: "pointer", textAlign: "left",
      fontSize: "var(--font-size-sm)", fontWeight: 500,
      color: danger ? "var(--status-danger)" : "var(--text-secondary)",
      transition: "var(--transition-fast)",
    }}
  >
    <span style={{ color: danger ? "var(--status-danger)" : "var(--text-tertiary)", display: "inline-flex" }}>
      {icon}
    </span>
    {children}
  </button>
);

export default TopNav;
